import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Trash2, X } from "lucide-react";
import { calendarApi, chatApi } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { requestMiraIntent } from "@/features/mira/miraIntentBus";
import { Button, Field, Input, Select, Textarea } from "@/components/ui";
import { formatDate, formatTime, resolveDisplayTimeZone } from "@/lib/dateFormat";

/**
 * Calendar UX — the calendar surface (rendered inside a pop-up overlay from the launcher).
 * Views: MONTH (default) + AGENDA. Month = a 6-week grid of the current month (today highlighted,
 * month nav), events placed in day cells with "+N more" overflow. Agenda = the next 10 upcoming
 * events by start time, CROSS-MONTH (count-bounded, not window-bounded). Both include recurring
 * occurrences (Calendar-B expands them for the requested window). Rendered in the viewer's tz.
 * Clicking an event opens the detail/RSVP (reused EventCard); clicking an empty day opens the
 * reused New-event form seeded to that day. No new dependency.
 */
const EVENTS_POLL_MS = 45000;
const AGENDA_LIMIT = 10;      // Agenda answers "what's next" — the next N, regardless of month.
const AGENDA_FORWARD_DAYS = 180;  // forward window wide enough to yield AGENDA_LIMIT occurrences
const MONTH_CELL_MAX = 3;     // event chips shown per day cell before "+N more"
// HEB-2: these are backend VALUES (submitted verbatim). Their LABELS localize via t("calendar.*"),
// so changing locale changes what's shown but never what's sent.
const EVENT_TYPES = ["meeting", "observation", "class", "extracurricular"];
const RSVP_STATES = ["accepted", "declined", "tentative", "pending"];
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]; // Sun-first (matches he-IL week)

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };

function dayKey(iso, tz) {
  // 'YYYY-MM-DD' in the viewer's timezone (en-CA gives an ISO-ish date).
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: tz });
}
function localDayKey(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
function timeLabel(iso, tz, language) {
  return formatTime(iso, language, { hour: "2-digit", minute: "2-digit", timeZone: tz });
}
function localInputForDate(date, hour) {
  const d = new Date(date); d.setHours(hour, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CalendarPage({ onClose }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  // CAL-1: display in the viewer's LOCAL timezone (browser zone when no explicit user tz), not a
  // hardcoded UTC fallback. Storage stays UTC; only the render is corrected. This makes the grid,
  // agenda, chips, and detail agree with the modal + the Home week-strip (already browser-local).
  const tz = resolveDisplayTimeZone(user);
  const [view, setView] = useState("month");        // "month" (default) | "agenda"
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [creating, setCreating] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [initialDate, setInitialDate] = useState(null);  // seed New-event to a clicked day
  const [detailEvent, setDetailEvent] = useState(null);  // event detail/RSVP modal
  const [selectedDay, setSelectedDay] = useState(null);  // "+N more" -> a day's events

  // Month grid = 6 weeks (42 cells) from the Sunday on/before the 1st; fetch the whole visible
  // window so trailing/leading adjacent-month days show their events too. Agenda = a forward
  // window, then the first AGENDA_LIMIT by start (cross-month).
  const gridStart = useMemo(() => addDays(startOfMonth(monthAnchor), -startOfMonth(monthAnchor).getDay()), [monthAnchor]);
  const window = view === "month"
    ? { from: gridStart, to: addDays(gridStart, 42) }
    : { from: startOfDay(new Date()), to: addDays(startOfDay(new Date()), AGENDA_FORWARD_DAYS) };

  const eventsQuery = useQuery({
    queryKey: ["calendar-events", view, window.from.toISOString()],
    queryFn: () =>
      calendarApi.events({ from: window.from.toISOString(), to: window.to.toISOString() }).then((r) => r.data),
    refetchInterval: EVENTS_POLL_MS,
    refetchOnWindowFocus: true,
  });
  const events = eventsQuery.data?.events || [];

  const openNewEvent = (date) => { setEditEvent(null); setInitialDate(date || null); setCreating(true); };
  const openEdit = (e) => { setDetailEvent(null); setEditEvent(e); setInitialDate(null); setCreating(true); };
  const monthLabel = formatDate(monthAnchor, i18n.language, { month: "long", year: "numeric" });

  return (
    <div className="flex h-full flex-col" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-primary" aria-hidden="true" />
          <h1 className="text-lg font-bold text-slate-900">{t("calendar.title")}</h1>
          {/* CAL-1: the "UTC" tz badge was a confession that the grid rendered the wrong zone. Times
              are now simply correct (viewer-local) — no label needed. */}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button type="button" data-testid="calendar-view-month" onClick={() => setView("month")}
              className={`px-3 py-1.5 text-sm font-medium ${view === "month" ? "bg-primary text-white" : "bg-white text-slate-600"}`}>{t("calendar.viewMonth")}</button>
            <button type="button" data-testid="calendar-view-agenda" onClick={() => setView("agenda")}
              className={`px-3 py-1.5 text-sm font-medium ${view === "agenda" ? "bg-primary text-white" : "bg-white text-slate-600"}`}>{t("calendar.viewAgenda")}</button>
          </div>
          <Button data-testid="calendar-new-event" onClick={() => openNewEvent(null)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> {t("calendar.newEvent")}
          </Button>
          {onClose ? (
            <button type="button" data-testid="calendar-close" onClick={onClose} aria-label={t("calendar.closeCalendar")} className="ms-1 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      {view === "month" ? (
        <div className="flex items-center justify-between px-5 py-2">
          <div className="flex items-center gap-1">
            <button type="button" data-testid="calendar-month-prev" onClick={() => setMonthAnchor((m) => startOfMonth(addMonths(m, -1)))} aria-label={t("calendar.previousMonth")} className="rounded-md p-1 text-slate-500 hover:bg-slate-100"><ChevronLeft className="h-5 w-5 rtl:-scale-x-100" /></button>
            <button type="button" data-testid="calendar-month-next" onClick={() => setMonthAnchor((m) => startOfMonth(addMonths(m, 1)))} aria-label={t("calendar.nextMonth")} className="rounded-md p-1 text-slate-500 hover:bg-slate-100"><ChevronRight className="h-5 w-5 rtl:-scale-x-100" /></button>
            <span className="ms-1 text-sm font-semibold text-slate-800">{monthLabel}</span>
          </div>
          <button type="button" data-testid="calendar-month-today" onClick={() => setMonthAnchor(startOfMonth(new Date()))} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">{t("calendar.today")}</button>
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {view === "month" ? (
          <MonthView events={events} tz={tz} gridStart={gridStart} monthAnchor={monthAnchor}
            onEventClick={setDetailEvent} onNewEvent={openNewEvent} onShowDay={setSelectedDay} />
        ) : (
          <AgendaList events={events} tz={tz} onEventClick={setDetailEvent} />
        )}
      </div>

      {detailEvent ? (
        <EventDetailModal event={detailEvent} tz={tz} onEdit={openEdit} onClose={() => setDetailEvent(null)} />
      ) : null}
      {selectedDay ? (
        <DayEventsModal dayKeyStr={selectedDay} events={events} tz={tz}
          onEventClick={(e) => { setSelectedDay(null); setDetailEvent(e); }} onClose={() => setSelectedDay(null)} />
      ) : null}
      {creating ? (
        <EventForm tz={tz} event={editEvent} initialDate={initialDate} onClose={() => { setCreating(false); setEditEvent(null); setInitialDate(null); }} />
      ) : null}
    </div>
  );
}

function EventCard({ event, tz, onEdit }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["school_admin", "training_admin"].includes(user?.tenant_role);
  // PLAN-2 M5 — "Brief me" is offered to an admin when the event has exactly ONE
  // non-organizer attendee (the teacher this meeting is with). It rides the same
  // general Mira intent bus as "Review with Mira".
  const guests = (event.attendees || []).filter((a) => !a.is_organizer);
  const briefTarget = isAdmin && guests.length === 1 ? guests[0] : null;
  const rsvpMutation = useMutation({
    mutationFn: (rsvp) => calendarApi.rsvp(event.id, rsvp).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["calendar-events"] }),
  });

  return (
    <div data-testid="calendar-event" className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900"><bdi>{event.title}</bdi></span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] uppercase tracking-wide text-slate-500">{t("calendar.eventType." + event.event_type)}</span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            {event.all_day ? t("calendar.allDay") : `${timeLabel(event.start_at, tz, i18n.language)} – ${timeLabel(event.end_at, tz, i18n.language)}`}
            {event.location ? <> · <bdi>{event.location}</bdi></> : ""}
          </p>
          {event.attendees?.length ? (
            <p className="mt-1 text-[11px] text-slate-400">
              {event.attendees.map((a) => t("calendar.attendeeRsvpFormat", { name: a.name, rsvp: t("calendar.rsvp." + a.rsvp) })).join(", ")}
            </p>
          ) : null}
        </div>
        {event.is_organizer ? (
          <button type="button" data-testid="calendar-edit-event" onClick={() => onEdit(event)} className="text-xs font-semibold text-primary hover:text-primary/80">
            {t("calendar.edit")}
          </button>
        ) : null}
      </div>
      {briefTarget ? (
        <button
          type="button"
          data-testid="calendar-brief-me"
          onClick={() =>
            requestMiraIntent({
              type: "pre_conference_brief",
              teacherId: briefTarget.user_id,
              teacherName: briefTarget.name,
            })
          }
          className="mt-2 rounded-full bg-ink px-3 py-1 text-xs font-semibold text-white hover:bg-ink-2"
        >
          {t("calendar.briefMeWithMira")}
        </button>
      ) : null}
      {!event.is_organizer ? (
        <div className="mt-2 flex gap-1" data-testid="calendar-rsvp">
          {["accepted", "tentative", "declined"].map((state) => (
            <button
              key={state}
              type="button"
              data-testid={`calendar-rsvp-${state}`}
              onClick={() => rsvpMutation.mutate(state)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                event.my_rsvp === state ? "bg-primary text-white" : "border border-slate-300 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t("calendar.rsvp." + state)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function _eventsByDay(events, tz) {
  const map = new Map();
  for (const e of events) {
    const key = dayKey(e.start_at, tz);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  }
  for (const list of map.values()) list.sort((a, b) => a.start_at.localeCompare(b.start_at));
  return map;
}

function EventChip({ event, tz, onClick }) {
  const { i18n } = useTranslation();
  return (
    <button
      type="button"
      data-testid="calendar-event"
      data-recurring={event.is_recurring_instance ? "true" : "false"}
      data-mira={event.created_via === "mira" ? "true" : "false"}
      // DESIGN-2E — the gold SESSION signal is now CANONICAL: event_type === "cognivio_session",
      // identical to WeekStrip/MonthGrid (border-recognition + recognition-tint), so a session renders
      // gold on all three calendar surfaces. created_via stays only as the distinct MIRA-provenance
      // ring (a separate marker, never the session signal).
      data-session={event.event_type === "cognivio_session" ? "true" : "false"}
      onClick={(e) => { e.stopPropagation(); onClick(event); }}
      className={`block w-full truncate rounded px-1.5 py-0.5 text-start text-[11px] font-medium text-ink ${
        event.event_type === "cognivio_session"
          ? "border-2 border-recognition bg-recognition-tint/40"
          : "bg-ivory-2 hover:bg-gold-soft/60"
      } ${event.created_via === "mira" && event.event_type !== "cognivio_session" ? "ring-1 ring-inset ring-gold" : ""}`}
    >
      {event.all_day ? null : <span className="text-ink-2">{timeLabel(event.start_at, tz, i18n.language)} </span>}
      <bdi>{event.title}</bdi>
    </button>
  );
}

// Month grid — the DEFAULT view. 6 weeks × 7 days; today highlighted; events (incl. recurring
// occurrences) placed in their day cell; "+N more" overflow; empty-day click seeds a new event.
function MonthView({ events, tz, gridStart, monthAnchor, onEventClick, onNewEvent, onShowDay }) {
  const { t } = useTranslation();
  const byDay = useMemo(() => _eventsByDay(events, tz), [events, tz]);
  const cells = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart]);
  const todayKey = dayKey(new Date().toISOString(), tz);
  const anchorMonth = monthAnchor.getMonth();

  return (
    <div data-testid="calendar-month">
      <div className="grid grid-cols-7 border-b border-slate-100 pb-1">
        {WEEKDAY_KEYS.map((d) => <div key={d} className="px-1 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">{t("calendar.weekday." + d)}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-100">
        {cells.map((day) => {
          const key = localDayKey(day);
          const dayEvents = byDay.get(key) || [];
          const inMonth = day.getMonth() === anchorMonth;
          const isToday = key === todayKey;
          const shown = dayEvents.slice(0, MONTH_CELL_MAX);
          const overflow = dayEvents.length - shown.length;
          return (
            <div
              key={key}
              data-testid="calendar-month-day"
              role="button"
              tabIndex={0}
              onClick={() => onNewEvent(day)}
              onKeyDown={(e) => { if (e.key === "Enter") onNewEvent(day); }}
              className={`min-h-[92px] cursor-pointer bg-white p-1 transition hover:bg-indigo-50/30 ${inMonth ? "" : "bg-slate-50/70"}`}
            >
              <div className="mb-0.5 flex justify-end">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${isToday ? "bg-primary font-bold text-white" : inMonth ? "text-slate-600" : "text-slate-300"}`}
                  data-testid={isToday ? "calendar-today-cell" : undefined}>
                  {day.getDate()}
                </span>
              </div>
              <div className="space-y-0.5">
                {shown.map((e) => <EventChip key={e.occurrence_id || e.id} event={e} tz={tz} onClick={onEventClick} />)}
                {overflow > 0 ? (
                  <button type="button" data-testid="calendar-event-more" onClick={(ev) => { ev.stopPropagation(); onShowDay(key); }}
                    className="block w-full px-1.5 text-start text-[11px] font-medium text-slate-500 hover:text-slate-700">
                    {t("calendar.moreCount", { count: overflow })}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Agenda — the next AGENDA_LIMIT upcoming events by start, CROSS-MONTH (count-bounded). On Jul 31
// an Aug 1 event still appears here, even though the month grid shows July.
function AgendaList({ events, tz, onEventClick }) {
  const { t, i18n } = useTranslation();
  const upcoming = useMemo(
    () => [...events].sort((a, b) => a.start_at.localeCompare(b.start_at)).slice(0, AGENDA_LIMIT),
    [events],
  );
  if (upcoming.length === 0) {
    return <p data-testid="calendar-empty" className="mt-4 rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">{t("calendar.emptyAgenda")}</p>;
  }
  return (
    <div className="mt-2 space-y-1.5" data-testid="calendar-agenda">
      {upcoming.map((e) => (
        <button
          key={e.occurrence_id || e.id}
          type="button"
          data-testid="calendar-agenda-row"
          data-recurring={e.is_recurring_instance ? "true" : "false"}
          onClick={() => onEventClick(e)}
          className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-start transition hover:border-indigo-200 hover:bg-indigo-50/40"
        >
          <div className="w-24 flex-none text-xs text-slate-500">
            <div className="font-semibold text-slate-700">{formatDate(e.start_at, i18n.language, { month: "short", day: "numeric", timeZone: tz })}</div>
            <div>{e.all_day ? t("calendar.allDay") : timeLabel(e.start_at, tz, i18n.language)}</div>
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-slate-900"><bdi>{e.title}</bdi></div>
            {e.location ? <div className="truncate text-xs text-slate-500"><bdi>{e.location}</bdi></div> : null}
          </div>
        </button>
      ))}
    </div>
  );
}

// Event detail / RSVP — reuses the existing EventCard inside a small modal (no rebuild).
function EventDetailModal({ event, tz, onEdit, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" data-testid="calendar-event-detail"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-2 flex justify-end">
          <button type="button" data-testid="calendar-detail-close" onClick={onClose} aria-label={t("calendar.close")} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <EventCard event={event} tz={tz} onEdit={onEdit} />
      </div>
    </div>
  );
}

// A single day's full event list (from a "+N more" overflow click).
function DayEventsModal({ dayKeyStr, events, tz, onEventClick, onClose }) {
  const { t, i18n } = useTranslation();
  const dayEvents = events.filter((e) => dayKey(e.start_at, tz) === dayKeyStr).sort((a, b) => a.start_at.localeCompare(b.start_at));
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" data-testid="calendar-day-events"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">{formatDate(`${dayKeyStr}T12:00:00`, i18n.language, { weekday: "long", month: "long", day: "numeric" })}</h3>
          <button type="button" onClick={onClose} aria-label={t("calendar.close")} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-1">
          {dayEvents.map((e) => <EventChip key={e.occurrence_id || e.id} event={e} tz={tz} onClick={onEventClick} />)}
        </div>
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  // ISO(UTC) -> a value for <input type="datetime-local"> in the browser's local time.
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EventForm({ tz, event, initialDate, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(event);
  const [title, setTitle] = useState(event?.title || "");
  const [eventType, setEventType] = useState(event?.event_type || "meeting");
  const [allDay, setAllDay] = useState(Boolean(event?.all_day));
  // Seed start/end from the event (edit) or a clicked month day (new event, defaults 9–10am).
  const [start, setStart] = useState(
    event ? toLocalInput(event.start_at) : (initialDate ? localInputForDate(initialDate, 9) : "")
  );
  const [end, setEnd] = useState(
    event ? toLocalInput(event.end_at) : (initialDate ? localInputForDate(initialDate, 10) : "")
  );
  const [location, setLocation] = useState(event?.location || "");
  const [description, setDescription] = useState(event?.description || "");
  const [attendees, setAttendees] = useState(
    (event?.attendees || []).filter((a) => !a.is_organizer).map((a) => a.user_id).filter((id) => id !== event?.organizer_id)
  );

  // B — editing a recurring occurrence prompts for a scope; creating offers a rule builder.
  const isRecurringInstance = isEdit && Boolean(event?.is_recurring_instance || event?.recurrence);
  const [scope, setScope] = useState("this");
  const [recurring, setRecurring] = useState(false);
  const [freq, setFreq] = useState("weekly");
  const [interval, setInterval] = useState(1);
  const [byday, setByday] = useState([]);
  const [endType, setEndType] = useState("never");
  const [count, setCount] = useState(10);
  const [until, setUntil] = useState("");

  const contactsQuery = useQuery({
    queryKey: ["chat-contacts"],
    queryFn: () => chatApi.contacts().then((r) => r.data),
  });
  const contacts = contactsQuery.data?.contacts || [];

  const toIso = (localValue) => (localValue ? new Date(localValue).toISOString() : null);

  const buildRecurrence = () => {
    if (!recurring) return null;
    const end =
      endType === "count" ? { type: "count", count: Number(count) || 1 }
      : endType === "until" ? { type: "until", until }
      : { type: "never" };
    return { freq, interval: Number(interval) || 1, byday: freq === "weekly" ? byday : [], end };
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        title: title.trim(),
        event_type: eventType,
        all_day: allDay,
        start_at: toIso(start),
        end_at: toIso(end || start),
        location,
        description,
        timezone: tz,
        attendee_ids: attendees,
      };
      if (!isEdit) payload.recurrence = buildRecurrence();
      if (isRecurringInstance) {
        payload.scope = scope;
        if (scope !== "all") payload.original_start_at = event.original_start_at;
      }
      return isEdit
        ? calendarApi.updateEvent(event.id, payload).then((r) => r.data)
        : calendarApi.createEvent(payload).then((r) => r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      const params = isRecurringInstance
        ? { scope, ...(scope !== "all" ? { original_start_at: event.original_start_at } : {}) }
        : undefined;
      return calendarApi.deleteEvent(event.id, params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      onClose();
    },
  });

  const toggleByday = (d) => setByday((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const toggleAttendee = (id) =>
    setAttendees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="calendar-event-form">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold text-slate-900">{isEdit ? t("calendar.editEventTitle") : t("calendar.newEventTitle")}</h2>
          <button type="button" data-testid="calendar-form-close" onClick={onClose} aria-label={t("calendar.close")} className="text-slate-500 hover:text-slate-700">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <Field label={t("calendar.fieldTitle")} htmlFor="calendar-title">
            <Input id="calendar-title" data-testid="calendar-field-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("calendar.addTitlePlaceholder")} />
          </Field>

          <div className="flex flex-wrap items-end gap-3">
            <Field label={t("calendar.fieldType")} htmlFor="calendar-type" className="min-w-[10rem]">
              <Select id="calendar-type" data-testid="calendar-field-type" value={eventType} onChange={(e) => setEventType(e.target.value)}>
                {EVENT_TYPES.map((type) => <option key={type} value={type}>{t("calendar.eventType." + type)}</option>)}
              </Select>
            </Field>
            <label className="flex h-10 items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" data-testid="calendar-field-allday" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30" />
              {t("calendar.allDay")}
            </label>
          </div>

          <div className="flex flex-wrap gap-3">
            <Field label={t("calendar.fieldStart")} htmlFor="calendar-start" className="flex-1 min-w-[10rem]">
              <Input id="calendar-start" type="datetime-local" data-testid="calendar-field-start" value={start} onChange={(e) => setStart(e.target.value)} />
            </Field>
            <Field label={t("calendar.fieldEnd")} htmlFor="calendar-end" className="flex-1 min-w-[10rem]">
              <Input id="calendar-end" type="datetime-local" data-testid="calendar-field-end" value={end} onChange={(e) => setEnd(e.target.value)} />
            </Field>
          </div>

          <Field label={t("calendar.fieldLocation")} htmlFor="calendar-location">
            <Input id="calendar-location" data-testid="calendar-field-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t("calendar.addLocationPlaceholder")} />
          </Field>
          <Field label={t("calendar.fieldDescription")} htmlFor="calendar-description">
            <Textarea id="calendar-description" data-testid="calendar-field-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("calendar.addNotesPlaceholder")} rows={3} />
          </Field>

          {/* B — scope chooser when editing/deleting a recurring occurrence. */}
          {isRecurringInstance ? (
            <div data-testid="calendar-scope-chooser" className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-900">{t("calendar.recurringScopePrompt")}</p>
              <div className="flex flex-col gap-1.5 text-sm text-slate-700">
                {[["this", "calendar.scopeThis"], ["this_and_following", "calendar.scopeThisAndFollowing"], ["all", "calendar.scopeAll"]].map(([val, labelKey]) => (
                  <label key={val} className="flex cursor-pointer items-center gap-2">
                    <input type="radio" name="calendar-scope" data-testid={`calendar-scope-${val}`} checked={scope === val} onChange={() => setScope(val)} className="h-4 w-4 text-primary focus:ring-primary/30" />
                    {t(labelKey)}
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {/* B — recurrence rule builder (create only). */}
          {!isEdit ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="calendar-recurrence-builder">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-800">
                <input type="checkbox" data-testid="calendar-recur-toggle" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30" />
                {t("calendar.repeats")}
              </label>
              {recurring ? (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                    <span className="text-slate-500">{t("calendar.recurEvery")}</span>
                    <Input type="number" min="1" size="sm" data-testid="calendar-recur-interval" value={interval} onChange={(e) => setInterval(e.target.value)} className="w-16" />
                    <Select size="sm" data-testid="calendar-recur-freq" value={freq} onChange={(e) => setFreq(e.target.value)} className="w-auto">
                      <option value="daily">{t("calendar.freqDaily")}</option>
                      <option value="weekly">{t("calendar.freqWeekly")}</option>
                      <option value="monthly">{t("calendar.freqMonthly")}</option>
                    </Select>
                  </div>
                  {freq === "weekly" ? (
                    <div className="flex flex-wrap gap-1.5" data-testid="calendar-recur-byday">
                      {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((d) => (
                        <button key={d} type="button" data-testid={`calendar-recur-day-${d}`} onClick={() => toggleByday(d)} className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${byday.includes(d) ? "bg-primary text-white" : "border border-slate-300 text-slate-600 hover:bg-white"}`}>
                          {t("calendar.byday." + d)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                    <span className="text-slate-500">{t("calendar.recurEnds")}</span>
                    <Select size="sm" data-testid="calendar-recur-end" value={endType} onChange={(e) => setEndType(e.target.value)} className="w-auto">
                      <option value="never">{t("calendar.recurEndNever")}</option>
                      <option value="count">{t("calendar.recurEndCount")}</option>
                      <option value="until">{t("calendar.recurEndUntil")}</option>
                    </Select>
                    {endType === "count" ? (
                      <Input type="number" min="1" size="sm" data-testid="calendar-recur-count" value={count} onChange={(e) => setCount(e.target.value)} className="w-16" />
                    ) : null}
                    {endType === "until" ? (
                      <Input type="date" size="sm" data-testid="calendar-recur-until" value={until} onChange={(e) => setUntil(e.target.value)} className="w-auto" />
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("calendar.attendees")}</p>
            <div className="max-h-36 space-y-0.5 overflow-y-auto rounded-xl border border-slate-200 p-2">
              {contacts.length === 0 ? (
                <p className="px-2 py-3 text-sm text-slate-400">{t("calendar.noColleagues")}</p>
              ) : (
                contacts.map((c) => (
                  <label key={c.id} data-testid="calendar-attendee-option" className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-50">
                    <input type="checkbox" checked={attendees.includes(c.id)} onChange={() => toggleAttendee(c.id)} className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30" />
                    <bdi>{c.name}</bdi>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/60 px-5 py-3">
          {isEdit ? (
            <button type="button" data-testid="calendar-delete-event" onClick={() => deleteMutation.mutate()} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 hover:text-rose-700">
              <Trash2 className="h-4 w-4" aria-hidden="true" /> {t("calendar.delete")}
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" data-testid="calendar-cancel-event" onClick={onClose} className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
              {t("calendar.cancel")}
            </button>
            <button
              type="button"
              data-testid="calendar-save-event"
              onClick={() => saveMutation.mutate()}
              disabled={!title.trim() || !start || saveMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:opacity-50"
            >
              {isEdit ? t("calendar.saveChanges") : t("calendar.createEvent")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
